import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";
import { readCache, writeCache, cacheFresh } from "./useCachedQuery";

// 24h TTL — preferences change rarely (settings screen visit only).
// Mutation paths in setPref/reset write through to cache so the user's
// own changes appear instantly without a round-trip.
const CACHE_KEY = (uid) => `notifPrefs.v1:${uid}`;
const TTL_MS    = 24 * 60 * 60 * 1000;

// Client-side mirror of the DEFAULTs in notification_preferences
// (migration 0133). A user with no row in the table inherits these;
// the DB's should_notify() function hard-codes the same shape.
export const DEFAULT_PREFERENCES = Object.freeze({
  pantry_activity:       false,
  shopping_activity:     true,
  meal_coordination:     true,
  cook_log_diners:       true,
  prep_reminders:        true,
  receipt_activity:      true,
  pantry_scan_activity:  true,
  cook_step_timers:      true,
});

/**
 * Load + update the signed-in user's notification_preferences row.
 * Returns { preferences, loading, error, setPref(key, value), reset() }.
 * `preferences` is never null — missing rows fall through to
 * DEFAULT_PREFERENCES so the UI always has something to render.
 */
export function useNotificationPreferences(userId) {
  const cached = userId ? readCache(CACHE_KEY(userId), TTL_MS) : null;
  const [preferences, setPreferences] = useState(() => cached ? { ...DEFAULT_PREFERENCES, ...cached } : DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(() => userId ? cached === null : false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    if (cacheFresh(CACHE_KEY(userId), TTL_MS)) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      const { data, error: e } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (!alive) return;
      if (e) setError(e);
      const merged = { ...DEFAULT_PREFERENCES, ...(data || {}) };
      setPreferences(merged);
      writeCache(CACHE_KEY(userId), data || {});
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [userId]);

  const setPref = useCallback(async (patch) => {
    if (!userId) return;
    // Optimistic merge.
    setPreferences(prev => ({ ...prev, ...patch }));
    const row = { user_id: userId, ...patch };
    const { error: e } = await supabase
      .from("notification_preferences")
      .upsert(row, { onConflict: "user_id" });
    if (e) setError(e);
  }, [userId]);

  const reset = useCallback(async () => {
    if (!userId) return;
    setPreferences(DEFAULT_PREFERENCES);
    const { error: e } = await supabase
      .from("notification_preferences")
      .upsert({ user_id: userId, ...DEFAULT_PREFERENCES }, { onConflict: "user_id" });
    if (e) setError(e);
  }, [userId]);

  return { preferences, loading, error, setPref, reset };
}
